// src/pages/SocialSharedCollection.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
   Container,
   Box,
   Typography,
   Card,
   CardMedia,
   CardContent,
   Grid,
   Chip,
   Paper,
   CircularProgress,
   Alert,
   Button,
   IconButton,
   Dialog,
   DialogContent,
   DialogTitle,
   DialogActions,
   TextField,
   Tooltip,
   Snackbar,
   useTheme,
   useMediaQuery,
   Fab,
} from '@mui/material';
import {
   ArrowBack as ArrowBackIcon,
   Share as ShareIcon,
   Close as CloseIcon,
   ChevronLeft as ChevronLeftIcon,
   ChevronRight as ChevronRightIcon,
   Favorite as FavoriteIcon,
   Edit as EditIcon,
   Save as SaveIcon,
   PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import SharedCollectionService from '../services/sharedCollectionService';
import { SharedCollection } from '../types/SharedCollection';
import PhotoLikeButton from '../components/common/PhotoLikeButton';
import { getAuthHeaders } from '../utils/auth';

const SocialSharedCollection: React.FC = () => {
   const { shareToken } = useParams<{ shareToken: string }>();
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const { currentUser } = useUser();

   // Debug current user state
   console.log('SocialSharedCollection: currentUser:', currentUser);
   console.log('SocialSharedCollection: currentUser?.uid:', currentUser?.uid);
   console.log('SocialSharedCollection: !currentUser?.uid:', !currentUser?.uid);

   const [collection, setCollection] = useState<SharedCollection | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
   const [editTitleModalOpen, setEditTitleModalOpen] = useState(false);
   const [editingTitle, setEditingTitle] = useState('');
   const [toastOpen, setToastOpen] = useState(false);
   const [toastMessage, setToastMessage] = useState('');

   useEffect(() => {
      if (shareToken) {
         fetchCollection();
      }
   }, [shareToken]);

   // Force component re-render to clear any caching issues
   useEffect(() => {
      const interval = setInterval(() => {
         // This forces a re-render every 10 seconds to clear any caching
         if (collection) {
            setCollection((prev) => (prev ? { ...prev } : null));
         }
      }, 10000);
      return () => clearInterval(interval);
   }, [collection]);

   const fetchCollection = async () => {
      try {
         setLoading(true);
         const sharedCollection = await SharedCollectionService.getSharedCollectionByToken(
            shareToken!,
            currentUser?.email || undefined
         );

         if (!sharedCollection) {
            setError('Collection not found or has been removed.');
            return;
         }

         console.log('SocialSharedCollection - Fetched collection:', sharedCollection);
         console.log(
            'SocialSharedCollection - Photos with like data:',
            sharedCollection.photos?.map((p) => ({
               id: p.originalPhotoId,
               likeCount: p.likeCount,
               isLiked: p.isLiked,
            }))
         );

         setCollection(sharedCollection);
      } catch (error) {
         console.error('Error fetching shared collection:', error);
         setError('Failed to load collection');
      } finally {
         setLoading(false);
      }
   };

   const getEntityTypeIcon = (entityType: string) => {
      switch (entityType) {
         case 'trip':
            return '✈️';
         case 'event':
            return '🎉';
         case 'album':
            return '📸';
         default:
            return '📷';
      }
   };

   const handlePhotoClick = (index: number) => {
      setSelectedPhotoIndex(index);
   };

   const handleCloseModal = () => {
      setSelectedPhotoIndex(null);
   };

   const handlePrevPhoto = () => {
      if (selectedPhotoIndex !== null && collection) {
         setSelectedPhotoIndex(
            selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : collection.photos.length - 1
         );
      }
   };

   const handleNextPhoto = () => {
      if (selectedPhotoIndex !== null && collection) {
         setSelectedPhotoIndex(
            selectedPhotoIndex < collection.photos.length - 1 ? selectedPhotoIndex + 1 : 0
         );
      }
   };

   const handleEditTitle = () => {
      if (collection) {
         setEditingTitle(
            collection.title || collection.subject || `Photos from ${collection.entityTitle}`
         );
         setEditTitleModalOpen(true);
      }
   };

   const handleSaveTitle = async () => {
      if (!collection || !editingTitle.trim()) {
         setToastMessage('Title cannot be empty');
         setToastOpen(true);
         return;
      }

      try {
         const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/shared-collections/${collection.id}/title`,
            {
               method: 'PATCH',
               headers: {
                  'Content-Type': 'application/json',
                  ...(await getAuthHeaders()),
               },
               body: JSON.stringify({ title: editingTitle.trim() }),
            }
         );

         if (!response.ok) {
            throw new Error('Failed to update collection title');
         }

         // Update local state
         setCollection((prev) => (prev ? { ...prev, title: editingTitle.trim() } : null));
         handleCloseEditTitleModal();
         setToastMessage('Collection title updated successfully');
         setToastOpen(true);
      } catch (error) {
         console.error('Error updating collection title:', error);
         setToastMessage('Failed to update collection title');
         setToastOpen(true);
      }
   };

   const handleCloseEditTitleModal = () => {
      setEditTitleModalOpen(false);
      setEditingTitle('');
   };

   const handleToastClose = () => {
      setToastOpen(false);
   };

   const handleShare = async () => {
      if (navigator.share) {
         try {
            await navigator.share({
               title: collection?.title || 'Shared Photo Collection',
               text: `Check out these ${collection?.photos.length} photos!`,
               url: window.location.href,
            });
         } catch (error) {
            console.log('Error sharing:', error);
         }
      } else {
         // Fallback: copy to clipboard
         navigator.clipboard.writeText(window.location.href);
         // You could show a toast here
      }
   };

   if (loading) {
      return (
         <Container maxWidth='lg'>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='50vh'>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='error'>{error}</Alert>
               <Box mt={2} textAlign='center'>
                  <Button variant='contained' onClick={() => navigate('/')}>
                     Go to Home
                  </Button>
               </Box>
            </Box>
         </Container>
      );
   }

   if (!collection) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='warning'>Collection not found</Alert>
            </Box>
         </Container>
      );
   }

   return (
      <Container maxWidth='lg'>
         <Box py={1}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
               <Box display='flex' alignItems='center' gap={2} mb={2}>
                  <Typography variant='body1' sx={{ fontSize: '1.2rem' }}>
                     {getEntityTypeIcon(collection.entityType)}
                  </Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ flex: 1 }}>
                     {collection.title ||
                        collection.subject ||
                        `Photos from ${collection.entityTitle}`}
                  </Typography>
                  {/* Show edit button only if current user is the collection owner */}
                  {currentUser && collection.createdBy === currentUser.uid && (
                     <Tooltip title='Edit collection title'>
                        <IconButton onClick={handleEditTitle} color='primary'>
                           <EditIcon />
                        </IconButton>
                     </Tooltip>
                  )}
                  <IconButton onClick={handleShare} color='primary'>
                     <ShareIcon />
                  </IconButton>
               </Box>

               {collection.message && (
                  <Box mt={2} p={2} bgcolor='grey.50' borderRadius={1}>
                     <Typography variant='body1' style={{ whiteSpace: 'pre-wrap' }}>
                        {collection.message}
                     </Typography>
                  </Box>
               )}

               <Box mt={2} display='flex' gap={1} alignItems='center' flexWrap='wrap'>
                  <Chip
                     label={`${collection.photos.length} photos`}
                     color='primary'
                     size='small'
                     icon={<FavoriteIcon />}
                  />
                  <Chip label='Public Collection' variant='outlined' size='small' />
               </Box>
            </Paper>

            {/* Photo Grid */}
            <Grid container spacing={1}>
               {collection.photos.map((photo, index) => {
                  console.log(`Photo ${index}:`, {
                     id: photo.originalPhotoId,
                     likeCount: photo.likeCount,
                     isLiked: photo.isLiked,
                     fullPhoto: photo,
                  });
                  return (
                     <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={photo.id}>
                        <Card
                           sx={{
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                 transform: 'translateY(-2px)',
                                 boxShadow: theme.shadows[4],
                              },
                           }}
                        >
                           <Box sx={{ cursor: 'pointer' }} onClick={() => handlePhotoClick(index)}>
                              <CardMedia
                                 component='img'
                                 height={isMobile ? 100 : 120}
                                 image={photo.thumbnailUrl || photo.url}
                                 alt={photo.title || 'Photo'}
                                 sx={{
                                    objectFit: 'cover',
                                    backgroundColor: 'grey.100',
                                 }}
                              />
                           </Box>
                           <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                              <Box display='flex' justifyContent='flex-start' alignItems='center'>
                                 <PhotoLikeButton
                                    photoId={photo.originalPhotoId}
                                    userId={currentUser?.uid || ''}
                                    context={{ type: 'collection', id: collection.id }}
                                    initialLikeCount={photo.likeCount || 0}
                                    initialIsLiked={photo.isLiked || false}
                                    size='small'
                                    variant='compact'
                                    showCount={true}
                                    disabled={false}
                                    onLikeChange={(isLiked, likeCount) => {
                                       // Update the photo in the collection state
                                       setCollection((prev) => {
                                          if (!prev) return prev;
                                          const updatedPhotos = prev.photos.map((p) =>
                                             p.originalPhotoId === photo.originalPhotoId
                                                ? { ...p, isLiked, likeCount }
                                                : p
                                          );
                                          return { ...prev, photos: updatedPhotos };
                                       });
                                    }}
                                 />
                              </Box>
                              {photo.description && (
                                 <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    sx={{
                                       display: '-webkit-box',
                                       WebkitLineClamp: 2,
                                       WebkitBoxOrient: 'vertical',
                                       overflow: 'hidden',
                                       mt: 0.5,
                                    }}
                                 >
                                    {photo.description}
                                 </Typography>
                              )}
                           </CardContent>
                        </Card>
                     </Grid>
                  );
               })}
            </Grid>

            {/* Photo Modal */}
            <Dialog
               open={selectedPhotoIndex !== null}
               onClose={handleCloseModal}
               maxWidth='lg'
               fullWidth
               sx={{
                  '& .MuiDialog-paper': {
                     bgcolor: 'black',
                     m: isMobile ? 1 : 2,
                  },
               }}
            >
               <DialogContent sx={{ p: 0, position: 'relative' }}>
                  {selectedPhotoIndex !== null && collection && (
                     <>
                        <IconButton
                           onClick={handleCloseModal}
                           sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: 'white',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              zIndex: 2,
                              '&:hover': {
                                 bgcolor: 'rgba(0,0,0,0.7)',
                              },
                           }}
                        >
                           <CloseIcon />
                        </IconButton>

                        <Box display='flex' alignItems='center' minHeight='70vh'>
                           <IconButton
                              onClick={handlePrevPhoto}
                              sx={{
                                 position: 'absolute',
                                 left: 8,
                                 color: 'white',
                                 bgcolor: 'rgba(0,0,0,0.5)',
                                 zIndex: 2,
                                 '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                 },
                              }}
                           >
                              <ChevronLeftIcon />
                           </IconButton>

                           <Box
                              component='img'
                              src={collection.photos[selectedPhotoIndex].url}
                              alt={collection.photos[selectedPhotoIndex].title || 'Photo'}
                              sx={{
                                 width: '100%',
                                 height: 'auto',
                                 maxHeight: '70vh',
                                 objectFit: 'contain',
                                 margin: 'auto',
                              }}
                           />

                           <IconButton
                              onClick={handleNextPhoto}
                              sx={{
                                 position: 'absolute',
                                 right: 8,
                                 color: 'white',
                                 bgcolor: 'rgba(0,0,0,0.5)',
                                 zIndex: 2,
                                 '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                 },
                              }}
                           >
                              <ChevronRightIcon />
                           </IconButton>
                        </Box>

                        {/* Photo Info */}
                        <Box
                           sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                              color: 'white',
                              p: 2,
                           }}
                        >
                           <Box display='flex' justifyContent='space-between' alignItems='center'>
                              <Typography variant='body2' sx={{ opacity: 0.9 }}>
                                 {selectedPhotoIndex + 1} of {collection.photos.length}
                              </Typography>
                              <PhotoLikeButton
                                 photoId={collection.photos[selectedPhotoIndex].originalPhotoId}
                                 userId={currentUser?.uid || ''}
                                 context={{ type: 'collection', id: collection.id }}
                                 initialLikeCount={
                                    collection.photos[selectedPhotoIndex].likeCount || 0
                                 }
                                 initialIsLiked={
                                    collection.photos[selectedPhotoIndex].isLiked || false
                                 }
                                 size='medium'
                                 variant='compact'
                                 showCount={true}
                                 disabled={false}
                                 onLikeChange={(isLiked, likeCount) => {
                                    // Update the photo in the collection state
                                    setCollection((prev) => {
                                       if (!prev) return prev;
                                       const updatedPhotos = prev.photos.map((p) =>
                                          p.originalPhotoId ===
                                          collection.photos[selectedPhotoIndex].originalPhotoId
                                             ? { ...p, isLiked, likeCount }
                                             : p
                                       );
                                       return { ...prev, photos: updatedPhotos };
                                    });
                                 }}
                              />
                           </Box>
                        </Box>
                     </>
                  )}
               </DialogContent>
            </Dialog>

            {/* Edit Title Modal */}
            <Dialog
               open={editTitleModalOpen}
               onClose={handleCloseEditTitleModal}
               maxWidth='sm'
               fullWidth
            >
               <DialogTitle>
                  <Box display='flex' alignItems='center' gap={1}>
                     <EditIcon />
                     Edit Collection Title
                  </Box>
               </DialogTitle>
               <DialogContent>
                  <TextField
                     autoFocus
                     margin='dense'
                     label='Collection Title'
                     type='text'
                     fullWidth
                     variant='outlined'
                     value={editingTitle}
                     onChange={(e) => setEditingTitle(e.target.value)}
                     onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                           handleSaveTitle();
                        }
                     }}
                     helperText='Enter a descriptive title for your photo collection'
                     sx={{ mt: 1 }}
                  />
               </DialogContent>
               <DialogActions>
                  <Button onClick={handleCloseEditTitleModal} color='inherit'>
                     Cancel
                  </Button>
                  <Button
                     onClick={handleSaveTitle}
                     variant='contained'
                     color='primary'
                     disabled={!editingTitle.trim()}
                     startIcon={<SaveIcon />}
                  >
                     Save Title
                  </Button>
               </DialogActions>
            </Dialog>

            {/* Toast Notification */}
            <Snackbar
               open={toastOpen}
               autoHideDuration={4000}
               onClose={handleToastClose}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
               message={toastMessage}
            />

            {/* Floating Signup Button - Visible to all users */}
            <Fab
               variant='extended'
               color='primary'
               onClick={() => navigate('/signup')}
               sx={{
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                  zIndex: 1000,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  minHeight: '56px',
                  '&:hover': {
                     background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                     transform: 'scale(1.05)',
                     boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                     '0%': {
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                     },
                     '50%': {
                        boxShadow: '0 8px 32px rgba(1, 148, 154, 0.4)',
                     },
                     '100%': {
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                     },
                  },
               }}
            >
               <PersonAddIcon sx={{ mr: 1 }} />
               Join Wetrippen
            </Fab>
         </Box>
      </Container>
   );
};

export default SocialSharedCollection;
